import React, { useCallback, useEffect, useRef, useState } from 'react';

import { apiDeleteAsUser, apiGetAsUser, apiPostAsUser, responseErrorMessage } from '../../../lib/api_v2';
import { getDesktopBridge, getDesktopOauthCapabilities, openDesktopOauth } from '../../../lib/desktopOauth';
import { logError } from '../../../lib/logger';

import useDesktopWindowFocusSignal from '../../../hooks/useDesktopWindowFocusSignal';

const CARD = 'flex w-full flex-col gap-3 rounded-lg border-[0.5px] border-white bg-gray-50 p-4 dark:border-neutral-800 dark:bg-neutral-900';
const BUTTON =
  'shrink-0 cursor-pointer rounded-full bg-gray-200 px-3 py-2 text-cursed text-black hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700';
const DESKTOP_RETURN_POLL_INTERVAL_MS = 1000;
const DESKTOP_RETURN_POLL_MAX_ATTEMPTS = 5;

const ConnectedServicesPanel = ({ initialNotice = null }) => {
  const [providers, setProviders] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(initialNotice);
  const loadRequestId = useRef(0);
  const awaitingDesktopReturn = useRef(false);
  const desktopReturnPolling = useRef(false);
  const desktopReturnPollAttempts = useRef(0);
  const desktopFocusSignal = useDesktopWindowFocusSignal();

  const load = useCallback(async function loadConnectedServices() {
    const requestId = loadRequestId.current + 1;
    loadRequestId.current = requestId;
    setError('');
    try {
      const response = await apiGetAsUser('/account/connections');
      const data = await response.json();
      if (!response.ok || data.ok !== true) throw new Error(responseErrorMessage(data, 'Could not load connected services.'));
      if (requestId !== loadRequestId.current) return;
      setProviders(Array.isArray(data.data?.providers) ? data.data.providers : []);
    } catch (loadError) {
      if (requestId !== loadRequestId.current) return;
      logError('ConnectedServicesPanel', 'Failed to load connected services', loadError);
      setError('Could not load connected services.');
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      loadRequestId.current += 1;
    };
  }, [load]);

  useEffect(
    function refreshAfterDesktopReturn() {
      if (!desktopFocusSignal) return undefined;

      if (awaitingDesktopReturn.current) {
        awaitingDesktopReturn.current = false;
        desktopReturnPolling.current = true;
        desktopReturnPollAttempts.current = 0;
      }
      if (!desktopReturnPolling.current) return undefined;

      let cancelled = false;
      let timer = null;

      async function pollConnections() {
        desktopReturnPollAttempts.current += 1;
        await load();
        if (cancelled) return;

        if (desktopReturnPollAttempts.current >= DESKTOP_RETURN_POLL_MAX_ATTEMPTS) {
          desktopReturnPolling.current = false;
          return;
        }
        timer = window.setTimeout(pollConnections, DESKTOP_RETURN_POLL_INTERVAL_MS);
      }

      pollConnections();
      return function stopDesktopReturnPolling() {
        cancelled = true;
        if (timer) window.clearTimeout(timer);
      };
    },
    [desktopFocusSignal, load],
  );

  const authorize = useCallback(
    async function authorize(provider, connectionId = null) {
      const operationId = connectionId || provider;
      setBusyId(operationId);
      setError('');
      try {
        const bridge = getDesktopBridge();
        const capabilities = bridge ? await getDesktopOauthCapabilities(bridge) : null;
        const response = await apiPostAsUser('/account/connection_authorizations', {
          provider,
          ...(connectionId ? { connection_id: connectionId } : {}),
          client: bridge ? 'desktop' : 'web',
          ...(capabilities?.authenticationSession && capabilities?.httpsCallbacks
            ? { completion_transport: 'authentication_session' }
            : {}),
        });
        const data = await response.json();
        if (!response.ok || data.ok !== true || !data.data?.authorization_url) {
          throw new Error(responseErrorMessage(data, 'Could not start authorization.'));
        }
        if (!bridge) {
          window.location.assign(data.data.authorization_url);
          return;
        }

        const completion = await openDesktopOauth({
          bridge,
          startUrl: data.data.authorization_url,
          callbackUrl: data.data.callback_url,
          capabilities,
        });
        if (completion?.completed) {
          let status = completion.completion?.status || (completion.succeeded ? 'connected' : 'failed');
          const completionUrl = completion.completion?.url;
          if (completionUrl) status = new URL(completionUrl).searchParams.get('status') || status;
          setNotice({ provider, status, connectionId });
          await load();
        } else awaitingDesktopReturn.current = true;
      } catch (authorizationError) {
        logError('ConnectedServicesPanel', 'Failed to authorize connected service', authorizationError);
        setError('Could not start authorization.');
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const disconnect = useCallback(
    async function disconnect(connection) {
      if (!window.confirm(`Disconnect ${connection.account_label} from all workspaces? Widgets and assistants will lose access to this account.`)) return;
      setBusyId(connection.id);
      setError('');
      try {
        const response = await apiDeleteAsUser(`/account/connections/${encodeURIComponent(connection.id)}`);
        const data = await response.json();
        if (!response.ok || data.ok !== true) throw new Error(responseErrorMessage(data, 'Could not disconnect account.'));
        await load();
      } catch (disconnectError) {
        logError('ConnectedServicesPanel', 'Failed to disconnect connected service', disconnectError);
        setError('Could not disconnect account.');
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const handleAuthorize = useCallback(
    function handleAuthorize(event) {
      const { provider, connectionId } = event.currentTarget.dataset;
      authorize(provider, connectionId || null);
    },
    [authorize],
  );

  const handleDisconnect = useCallback(
    function handleDisconnect(event) {
      const { connectionId, accountLabel } = event.currentTarget.dataset;
      disconnect({ id: connectionId, account_label: accountLabel });
    },
    [disconnect],
  );

  if (!providers && !error) return <p className="text-cursed text-gray-500">Loading connected services…</p>;
  if (!providers) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-cursed text-red-600">{error}</p>
        <button type="button" className={BUTTON} onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  const noticeText =
    notice?.status === 'connected'
      ? 'Connected successfully.'
      : notice?.status === 'wrong_account'
        ? 'Wrong account selected. Your existing connection was not changed.'
        : notice?.status === 'cancelled'
          ? 'Connection cancelled. Your existing connection was not changed.'
          : notice?.status === 'failed'
            ? 'Could not connect that account. Your existing connection was not changed.'
            : null;

  return (
    <div className="flex w-full flex-col gap-5 pt-6">
      <p className="text-[13px] leading-5 text-gray-500 dark:text-neutral-400">
        Linking an account does not grant access to any workspace. Enable it separately in Workspace Options.
      </p>
      {noticeText && (
        <div className="flex items-center justify-between gap-3 text-cursed text-gray-600 dark:text-neutral-300">
          <span>{noticeText}</span>
          {notice.status === 'wrong_account' && notice.provider && notice.connectionId && (
            <button
              type="button"
              className={BUTTON}
              data-provider={notice.provider}
              data-connection-id={notice.connectionId}
              disabled={busyId !== null}
              onClick={handleAuthorize}
            >
              Retry
            </button>
          )}
        </div>
      )}
      {error && <p className="text-cursed text-red-600">{error}</p>}
      {providers.map((provider) => (
        <div key={provider.key} className={CARD}>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium text-black dark:text-neutral-100">{provider.title}</h3>
            <button
              type="button"
              className={BUTTON}
              disabled={!provider.configured || busyId !== null}
              data-provider={provider.key}
              onClick={handleAuthorize}
            >
              {provider.configured ? (provider.connections.length === 0 ? 'Link' : 'Link another') : 'Unavailable'}
            </button>
          </div>
          {provider.connections.map((connection) => (
            <div key={connection.id} className="flex items-center gap-3 border-t border-gray-200 pt-3 dark:border-neutral-800">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-black dark:text-neutral-100">{connection.account_label}</span>
                <span className="block text-xs text-gray-500">{connection.status.replaceAll('_', ' ')}</span>
              </span>
              <button
                type="button"
                className={BUTTON}
                disabled={!provider.configured || busyId !== null}
                data-provider={provider.key}
                data-connection-id={connection.id}
                onClick={handleAuthorize}
              >
                Reconnect
              </button>
              <button
                type="button"
                className={BUTTON}
                disabled={busyId !== null}
                data-connection-id={connection.id}
                data-account-label={connection.account_label}
                onClick={handleDisconnect}
              >
                Disconnect
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default ConnectedServicesPanel;
